import React, { ReactNode, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Global } from '@emotion/react';
// material
import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';

import { APPEARANCE_MODE_DARK, APPEARANCE_MODE_LIGHT, SPACING } from './constants';
import { CUSTOM_CONFIGURATION_OPTION_PREFIX } from '@/modules/common/constants/configuration';
import breakpoints from '@/mui/constants/breakpoints';
import { COMMON } from '@/mui/constants/palette';
import darkTheme from '@/mui/constants/palette/dark';
import lightTheme from '@/mui/constants/palette/light';
import shadows from '@/mui/constants/shadows';
import shape from '@/mui/constants/shape';
import typography from '@/mui/constants/typography';
import zIndex from '@/mui/constants/zIndex';

import { getCssVarsFromTheme } from './helpers';
// todo: migrate to mui css variables
// import { RootContainers } from '@/modules/common/helpers';
import * as commonSelectors from '@/modules/common/redux/selectors/common';

import components from './components/MuiOverrides';

type ThemeConfigProps = {
  children: ReactNode;
};

export type ThemeModeType = typeof APPEARANCE_MODE_LIGHT | typeof APPEARANCE_MODE_DARK;

const paletteCollection = {
  [APPEARANCE_MODE_LIGHT]: lightTheme,
  [APPEARANCE_MODE_DARK]: darkTheme,
};

function ThemeConfig(props: ThemeConfigProps) {
  const appearance = useSelector(commonSelectors.appearanceSelector) as ThemeModeType;
  const font = useSelector(commonSelectors.fontSelector) as string;
  const color = useSelector(commonSelectors.colorSelector) as string;
  const bg = useSelector(commonSelectors.bgSelector) as string;
  const configuration = useSelector(commonSelectors.configurationSelector);
  const rtl = useSelector(commonSelectors.rtlSelector);

  const [theme, themeCssProps] = useMemo(() => {
    const palette$ = paletteCollection[appearance];

    const options: ThemeOptions = {
      direction: rtl ? 'rtl' : 'ltr',
      // cssVariables: {
      //   rootSelector: ':host',
      //   cssVarPrefix: 'theme',
      // },
      unstable_sxConfig: {
        size: { cssProperty: false },
        shape: { cssProperty: false },
        square: { cssProperty: false },
      },
      palette: {
        ...configuration.colorPresets,
        [`${CUSTOM_CONFIGURATION_OPTION_PREFIX}colors`]: configuration.colors,
        ...palette$,
        text: {
          ...palette$.text,
          primary: color || palette$.text.primary,
        },
        background: {
          ...palette$.background,
          default: bg || palette$.background.default,
        },
        common: COMMON,
        neutral: {
          dark: palette$.shades[900],
          main: palette$.shades[900],
          light: palette$.shades[700],
          contrastText: palette$.shades[900],
        },
        grey: { ...palette$.shades },
      },
      spacing: SPACING,
      shape,
      typography: {
        ...configuration.typographies,
        ...typography,
      },
      breakpoints,
      shadows,
      zIndex,
    };

    const defaultTheme = createTheme({ ...options });

    const theme$ = createTheme({
      ...options,
      components: components(defaultTheme),
    });

    const themeVars$ = getCssVarsFromTheme(defaultTheme);

    return [theme$, themeVars$];
  }, [appearance, bg, font, color]);

  return (
    <ThemeProvider theme={theme} /* colorSchemeNode={RootContainers.getRootContainer()}*/>
      <Global
        styles={{
          ':host': {
            ...themeCssProps,
            direction: rtl ? 'rtl' : 'ltr',
            '--general-font-family': font,
          },
        }}
      />
      {props.children}
    </ThemeProvider>
  );
}

ThemeConfig.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeConfig;
